import { isDba } from '@borealis/aurora';
import { Trans } from '@lingui/react/macro';
import { StdUriTemplate } from '@std-uritemplate/std-uritemplate';
import { Button } from '@warp-ds/react';
import { useEffect } from 'react';
import type { EIDCfg } from '../../../types/config.js';
import { VerifiedUserLogo } from '../../components/verified-user-logo.js';
import { useLookupPulse } from '../../hooks/useLookupPulse.js';

type EIDVerificationPageProps = {
  config: EIDCfg;
};

export const EIDVerificationPage = ({ config }: EIDVerificationPageProps) => {
  const { trackPageView, trackClick } = useLookupPulse(config.model);
  const eidProvider = isDba(config.brand) ? 'MitID' : 'BankID';

  useEffect(() => {
    trackPageView();
  }, []);

  const handleVerify = () => {
    trackClick({
      intent: 'Register',
      name: 'Verify identity',
      object: { elementType: 'Button' },
    });

    const baseUrl = window.location.origin;

    // Use URL constructor for safer URL building
    const redirectUrl = new URL(config.redirectTo, baseUrl).href;

    // Use URLSearchParams for automatic encoding
    const cancelUrlObj = new URL(`${config.root}/eid`, baseUrl);
    cancelUrlObj.searchParams.set('model', config.model);
    if (config.orgId) {
      cancelUrlObj.searchParams.set('orgId', config.orgId);
    }
    cancelUrlObj.searchParams.set('redirectTo', config.redirectTo);
    const cancelUrl = cancelUrlObj.href;

    const verificationUrl = StdUriTemplate.expand(config.verificationPath, { redirectUrl, cancelUrl });
    window.location.assign(verificationUrl);
  };

  return (
    <main className="max-w-[700] mx-auto mb-32">
      <div className="flex flex-col">
        <h1>
          {config.mode === 'create' ? (
            <Trans id="mobility-item-insertion.eid-verification-page.heading.create">New ad</Trans>
          ) : (
            <Trans id="mobility-item-insertion.eid-verification-page.heading.edit">Edit ad</Trans>
          )}
        </h1>

        <div className="flex flex-col items-center mt-40">
          <div className="md:w-144 w-96 text-center">
            <VerifiedUserLogo />
          </div>

          <h2 className="text-center mt-24 mb-16">
            <Trans id="mobility-item-insertion.eid-verification-page.title">Verify your identity</Trans>
          </h2>

          <div className="text-center">
            {config.mode === 'create' ? (
              <Trans
                id="mobility-item-insertion.eid-verification-page.description.create"
                comment="Description for eID verification when creating an ad. {0} is the eID provider name (BankID or MitID)."
              >
                In order to create the ad, you need to verify with {eidProvider}.
              </Trans>
            ) : (
              <Trans
                id="mobility-item-insertion.eid-verification-page.description.edit"
                comment="Description for eID verification when editing an ad. {0} is the eID provider name (BankID or MitID)."
              >
                In order to edit the ad, you need to verify with {eidProvider}.
              </Trans>
            )}
          </div>

          <div className="w-full flex justify-center mt-24">
            <Button primary className="flex items-center gap-8 w-full sm:w-auto max-w-none" onClick={handleVerify}>
              <Trans
                id="mobility-item-insertion.eid-verification-page.validate-button"
                comment="Button text to initiate an EID flow. {0} is the eID provider name (BankID or MitID)."
              >
                Verify with {eidProvider}
              </Trans>
            </Button>
          </div>
        </div>
      </div>
    </main>
  );
};
